import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CornerDownLeft, Loader2, Search, X } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/AuthContext';
import { NAV_ITEMS, SUB_PAGE_LABELS, visibleNavItems } from '@/nav/navigation';

interface SearchItem {
  id: string;
  title: string;
  subtitle: string;
  to: string;
}

interface SearchGroup {
  group: string;
  label: string;
  items: SearchItem[];
}

interface Row extends SearchItem {
  groupLabel: string;
  key: string;
}

const RECENT_KEY = 'oneparacon.search.recent';

function readRecent(): Row[] {
  try {
    return JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]') as Row[];
  } catch {
    return [];
  }
}

function remember(row: Row) {
  try {
    const next = [row, ...readRecent().filter((entry) => entry.key !== row.key)].slice(0, 6);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Not remembering a search is no loss.
  }
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * The top bar's search.
 *
 * Type to find people, projects, companies, tenders, prospects, tools, stock,
 * SOPs — and, for the finance audience, orders and bills — each opening its
 * own record. Screens match by name too, so "timesheets" goes to Timesheets.
 * Arrow keys move, Enter opens, Escape closes, and Ctrl+K or ⌘K jumps here
 * from anywhere. Results come from the API through the same permissions the
 * modules use, so search never shows what a screen would not.
 */
export function GlobalSearch({ autoFocus = false, onNavigate }: { autoFocus?: boolean; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const term = useDebounced(query.trim(), 200);

  const results = useQuery({
    queryKey: ['search', term],
    queryFn: () => api.get<SearchGroup[]>('/search', { query: { q: term } }),
    enabled: term.length >= 2,
    staleTime: 30_000,
  });

  // Screens the person can open whose name matches.
  const pages = useMemo<Row[]>(() => {
    if (query.trim().length < 2) return [];
    const needle = query.trim().toLowerCase();
    const allowed = visibleNavItems({ feedback: Boolean(user?.feedback_access), executive: Boolean(user?.executive_access), finance: Boolean(user?.finance_access) });
    const allowedPaths = new Set(allowed.map((item) => item.path));
    const blocked = NAV_ITEMS.filter((item) => !allowedPaths.has(item.path)).map((item) => item.path);

    const entries = [
      ...allowed.map((item) => ({ label: item.label, path: item.path })),
      ...Object.entries(SUB_PAGE_LABELS)
        .filter(([path]) => !blocked.some((prefix) => path === prefix || path.startsWith(`${prefix}/`)))
        .map(([path, label]) => ({ label, path })),
    ];

    const seen = new Set<string>();
    return entries
      .filter((entry) => entry.label.toLowerCase().includes(needle))
      .filter((entry) => (seen.has(entry.path) ? false : (seen.add(entry.path), true)))
      .slice(0, 4)
      .map((entry) => ({ id: entry.path, key: `page:${entry.path}`, title: entry.label, subtitle: 'Go to screen', to: entry.path, groupLabel: 'Screens' }));
  }, [query, user]);

  const rows = useMemo<Row[]>(() => {
    if (query.trim().length < 2) return readRecent().map((row) => ({ ...row, groupLabel: 'Recent' }));
    const records = (term === query.trim() ? results.data ?? [] : results.data ?? []).flatMap((group) =>
      group.items.map((item) => ({ ...item, key: `${group.group}:${item.id}`, groupLabel: group.label })),
    );
    return [...pages, ...records];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recent searches read on each opening
  }, [query, term, results.data, pages, open]);

  useEffect(() => setActive(0), [query]);

  // Ctrl+K / ⌘K from anywhere.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);

  const go = (row: Row) => {
    remember({ ...row, groupLabel: row.groupLabel === 'Recent' ? row.groupLabel : row.groupLabel });
    setOpen(false);
    setQuery('');
    input.current?.blur();
    navigate(row.to);
    onNavigate?.();
  };

  const searching = term.length >= 2 && results.isFetching;
  const nothing = query.trim().length >= 2 && !searching && term === query.trim() && rows.length === 0;

  let lastGroup = '';

  return (
    <div ref={container} className="relative w-full">
      <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls="global-search-results"
        aria-activedescendant={open && rows[active] ? `search-row-${active}` : undefined}
        placeholder="Search people, projects, tenders…"
        aria-label="Search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActive((current) => Math.min(rows.length - 1, current + 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((current) => Math.max(0, current - 1));
          } else if (event.key === 'Enter' && rows[active]) {
            event.preventDefault();
            go(rows[active]!);
          } else if (event.key === 'Escape') {
            setOpen(false);
            input.current?.blur();
          }
        }}
        className="h-10 w-full rounded-lg border border-line bg-cloud/60 pl-9 pr-16 text-sm text-body transition-colors placeholder:text-muted focus:border-navy focus:bg-white focus:shadow-focus-navy focus:outline-none [&::-webkit-search-cancel-button]:hidden"
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {searching ? (
          <Loader2 aria-hidden className="h-4 w-4 animate-spin text-muted" />
        ) : query ? null : (
          <kbd className="rounded border border-line bg-white px-1.5 py-0.5 text-[10px] font-semibold text-muted">Ctrl K</kbd>
        )}
      </span>
      {query && (
        <button
          type="button"
          aria-label="Clear search"
          className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted hover:bg-cloud hover:text-body"
          onClick={() => {
            setQuery('');
            input.current?.focus();
          }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {open && (rows.length > 0 || nothing || (query.trim().length >= 2 && searching)) && (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1.5 max-h-[70vh] min-w-[340px] overflow-y-auto rounded-xl border border-line bg-white p-1.5 shadow-flyout scrollbar-slim"
        >
          {nothing && <p className="px-3 py-6 text-center text-sm text-muted">Nothing matches “{query.trim()}”.</p>}
          {rows.length === 0 && searching && <p className="px-3 py-6 text-center text-sm text-muted">Searching…</p>}
          {rows.map((row, index) => {
            const heading = row.groupLabel !== lastGroup ? row.groupLabel : null;
            lastGroup = row.groupLabel;
            return (
              <div key={row.key}>
                {heading && <p className="px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-[0.08em] text-muted">{heading}</p>}
                <button
                  id={`search-row-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  onPointerEnter={() => setActive(index)}
                  onClick={() => go(row)}
                  className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left', index === active ? 'bg-cloud' : 'hover:bg-cloud/60')}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">{row.title}</span>
                    {row.subtitle && <span className="block truncate text-helper text-muted">{row.subtitle}</span>}
                  </span>
                  {index === active ? (
                    <CornerDownLeft aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted" />
                  ) : (
                    <ArrowRight aria-hidden className="h-3.5 w-3.5 shrink-0 text-line" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
